import emailjs from '@emailjs/browser';

// Reuses the same EmailJS account/template already wired up and working in
// the main Hustlance app (src/components/pages/ContactPage.tsx) — these IDs
// are meant to be client-exposed by EmailJS's design (the public key is not
// a secret; authorization happens on EmailJS's side per-domain/template).
//
// IMPORTANT CAVEAT: this template was built for the main app's "contact form
// submission" use case (from_name/from_email/subject/message -> one fixed
// recipient). Reusing it here for newsletter confirmations and
// publish-notification emails means the exact wording/HTML framing the
// recipient sees is whatever that template's layout says — it wasn't
// purpose-built for these. If the emails don't read naturally, the fix is a
// new template in the EmailJS dashboard (https://dashboard.emailjs.com),
// with its ID swapped in below.
const EMAILJS_SERVICE_ID = 'service_gmei7z8';
const EMAILJS_TEMPLATE_ID = 'template_pk5koj8';
const EMAILJS_PUBLIC_KEY = 'sZY4CzxRVLqL8VMiJ';

interface SendEmailParams {
  to_email: string;
  subject: string;
  message: string;
  from_name?: string;
  from_email?: string;
}

export async function sendEmail(params: SendEmailParams): Promise<void> {
  await emailjs.send(
    EMAILJS_SERVICE_ID,
    EMAILJS_TEMPLATE_ID,
    {
      from_name: params.from_name ?? 'Hustlance Blog',
      from_email: params.from_email ?? 'no-reply@hustlance.com',
      inquiry_type: 'blog',
      subject: params.subject,
      message: params.message,
      to_email: params.to_email,
    },
    EMAILJS_PUBLIC_KEY
  );
}

/** Sends one email per recipient, sequentially with a small delay — EmailJS's
 * free tier throttles/caps volume, so this deliberately doesn't fire all
 * sends in parallel. Best-effort: a failure for one recipient doesn't stop
 * the rest. Returns how many succeeded. */
export async function sendBulkEmail(
  recipients: string[],
  build: (email: string) => SendEmailParams
): Promise<{ sent: number; failed: number }> {
  let sent = 0;
  let failed = 0;
  for (const email of recipients) {
    try {
      await sendEmail(build(email));
      sent++;
    } catch {
      failed++;
    }
    await new Promise(resolve => setTimeout(resolve, 300));
  }
  return { sent, failed };
}
